import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http'
import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideAppInitializer,
} from '@angular/core'
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async'
import { provideRouter, Router } from '@angular/router'

import * as Sentry from '@sentry/angular'
import { NgxCsvParserModule } from 'ngx-csv-parser'
import { MessageService } from 'primeng/api'
import { TooltipStyle } from 'primeng/tooltip'

import { ApiService } from '@ti/core/api'
import { ApiModule } from '@ti/core/api/api.module'
import { AuthModule } from '@ti/core/auth/auth.module'
import { BrowserPushClient, TspProfile } from '@ti/core/auth/services'
import { provideFirebaseApp } from '@ti/core/auth/utils/provide-fb-app'
import { AppCommonModule } from '@ti/core/common/common.module'
import {
  BrowserFileSaver,
  GlobalErrorHandler,
  provideBrowserNetworkStatusProvider,
  WhiteLabelSettingsProvider,
} from '@ti/core/common/services'
import { provideBrowserClipboard } from '@ti/core/common/services/clipboard'
import { provideBrowserExternalLinkOpener } from '@ti/core/common/services/external-link-opener.service'
import { BrowserScreenFacade } from '@ti/core/common/services/screen.facade'
import { provideBrowserServiceWorker } from '@ti/core/common/services/update.manager'
import { CONFIG } from '@ti/core/contracts/config'
import { FILE_SAVER } from '@ti/core/contracts/file-saver'
import { I18NConfigProvider } from '@ti/core/contracts/i18n'
import { PROFILE_AUTO_LOAD } from '@ti/core/contracts/profile'
import { PUSH_CLIENT } from '@ti/core/contracts/push'
import { SCREEN_FACADE } from '@ti/core/contracts/screen.facade'
import { provideAppVersion } from '@ti/core/contracts/version'
import { provideRealtimeTokenFetcher } from '@ti/core/realtime'
import { providePrimeNgPreset } from '@ti/core/themes/services'
import { provideLanguageInitializer, provideTranslationsLoader } from '@ti/core/ui-kit/i18n'
import { DIALOG_SERVICE_PROVIDER, provideDialogFacade } from '@ti/core/ui-kit/providers/globals'
import { UiKitModule } from '@ti/core/ui-kit/ui-kit.module'
import { GroupsOptionsProvider } from '@ti/shared/groups/services'
import { UserModule } from '@ti/shared/user/user.module'

import env from '../env'
import { APP_VERSION as version } from '../env/version'
import { AccountsModel } from './account/models/accounts.model'
import { BillingModesProvider } from './account/models/billing-modes.provider'
import { CountyOptionsProvider } from './account/models/country-options.provider'
import { provideAdminUsersManager } from './admin-users/services/user.manager'
import { AppInitializer } from './app.initializer'
import APP_ROUTES from './app.routes'
import { BillingAccountsModel } from './billing/models/billing-accounts.model'
import { provideTIUserSettingsModel } from './common/models/user-settings.model'
import { AUTH_PROVIDERS } from './common/services/auth'
import { LANG_PROVIDERS } from './common/services/lang'
import { AdminI18NConfigProvider } from './common/services/lang/admin-i18n-config.provider'
import { provideTiRealtimeClient } from './common/services/realtime.client'
import { AdminWhiteLabelSettingsProvider } from './common/services/white-label-settings.provider'
import { McpAuthContext } from './mcp/bootstrap/mcp-auth-context'

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(APP_ROUTES),

    provideBrowserServiceWorker('ngsw-worker.js', { enabled: env.envType !== 'local' }),
    importProvidersFrom(NgxCsvParserModule),
    importProvidersFrom(ApiModule),
    importProvidersFrom(AppCommonModule),
    importProvidersFrom(AuthModule),
    importProvidersFrom(UiKitModule),
    importProvidersFrom(UserModule),

    provideAnimationsAsync(),
    TspProfile,
    AccountsModel,
    BillingAccountsModel,
    CountyOptionsProvider,
    DIALOG_SERVICE_PROVIDER,
    MessageService,
    TooltipStyle,
    BillingModesProvider,
    provideLanguageInitializer(),
    provideTranslationsLoader(['ti', 'tsp', 'shared']),
    { provide: CONFIG, useValue: env },
    { provide: FILE_SAVER, useClass: BrowserFileSaver },
    { provide: PUSH_CLIENT, useClass: BrowserPushClient },
    { provide: SCREEN_FACADE, useClass: BrowserScreenFacade },
    { provide: ErrorHandler, useFactory: () => new GlobalErrorHandler() },
    { provide: Sentry.TraceService, deps: [Router] },
    provideAppInitializer(() => void inject(Sentry.TraceService)), // Init trace service
    ...AUTH_PROVIDERS,
    ...LANG_PROVIDERS,
    { provide: I18NConfigProvider, useClass: AdminI18NConfigProvider },
    { provide: PROFILE_AUTO_LOAD, useFactory: () => inject(McpAuthContext).profileEnabled$ },
    { provide: WhiteLabelSettingsProvider, useClass: AdminWhiteLabelSettingsProvider },
    { provide: GroupsOptionsProvider, useValue: undefined },
    provideAppVersion(version),
    provideFirebaseApp(),
    provideTiRealtimeClient(),
    provideAdminUsersManager(),
    provideTIUserSettingsModel(),
    provideDialogFacade(),
    provideHttpClient(withInterceptorsFromDi()),
    providePrimeNgPreset(),
    provideBrowserClipboard(),
    provideBrowserExternalLinkOpener(),
    provideBrowserNetworkStatusProvider(),
    // admin-api serves the Firebase custom token from its own user scope, not
    // from the Fleet one the realtime client defaults to.
    provideRealtimeTokenFetcher(() => {
      const api = inject(ApiService)
      return () => api.ti.user.getRealtimeToken()
    }),
    provideAppInitializer(() => {
      const initializer = inject(AppInitializer)
      return initializer.init()
    }),
  ],
}
